import {
  ANSWER_FORMAT_MODES,
  PROMPT_AUDIO,
  PROMPT_SOURCE_TEXT,
  PROMPT_TRANSLATION_TEXT,
} from '@/utils/testPromptAnswerMatrix'
import type { AnswerFormatMode, PromptTypeKey } from '@/utils/testPromptAnswerMatrix'

/**
 * Что умеет конкретная форма конструктора теста.
 *
 * Профиль, а не набор флагов по месту: секции общие для обеих форм, и без
 * одного описания «что здесь можно» различия расползались бы по компонентам —
 * так они уже разъезжались, пока формы жили двумя независимыми копиями.
 *
 * Ограничения ученика — зеркало серверных: устный ответ и произношение в
 * самостоятельном тесте оценивать некому
 * (`TestPromptAnswerListsRules::studentSelfTestConstraintErrorsFromDerived`),
 * а отображение «аудио» ему доступно.
 */
export type TestCreateProfile = {
  /** Префикс id полей: у двух форм на одной странице не должны совпадать `htmlFor`. */
  idPrefix: string
  /** Какие отображения карточки предлагать. */
  promptTypes: PromptTypeKey[]
  /** Какие форматы ответа предлагать: речь ученику оценивать некому. */
  answerFormatModes: AnswerFormatMode[]
  /** Можно ли выключать автопроверку: без учителя проверять ответ некому. */
  canDisableAutoCheck: boolean
  /** Показывать ли групповые настройки (исключение пересекающихся слов). */
  supportsGroups: boolean
  /** Можно ли выбирать конкретные карточки: поиск идёт по словарю учителя. */
  supportsExplicitCards: boolean
  /** Вход до начала урока: у самостоятельного теста урока нет. */
  supportsPrepMinutes: boolean
  /** Предлагать ли транскрипцию: её показывают только при устном ответе. */
  supportsTranscription: boolean
}

export const TEACHER_LESSON_PROFILE: TestCreateProfile = {
  idPrefix: 'lesson-test',
  promptTypes: [PROMPT_SOURCE_TEXT, PROMPT_TRANSLATION_TEXT, PROMPT_AUDIO],
  answerFormatModes: [...ANSWER_FORMAT_MODES],
  canDisableAutoCheck: true,
  supportsGroups: true,
  supportsExplicitCards: true,
  supportsPrepMinutes: true,
  supportsTranscription: true,
}

export const STUDENT_SELF_PROFILE: TestCreateProfile = {
  idPrefix: 'self-test',
  promptTypes: [PROMPT_SOURCE_TEXT, PROMPT_TRANSLATION_TEXT, PROMPT_AUDIO],
  // Речь сервер ученику не разрешает: оценивать её некому, распознавания речи
  // в продукте нет. Без этого поля форма предложила бы формат, который API
  // отбивает 422 уже на сохранении.
  answerFormatModes: ['typed', 'choose'],
  // Без автопроверки остаётся только устный ответ — выключатель ученику не
  // нужен и всё равно ни к чему не привёл бы.
  canDisableAutoCheck: false,
  supportsGroups: false,
  supportsExplicitCards: false,
  supportsPrepMinutes: false,
  // Транскрипцию платформа показывает только при устном ответе
  // (`mayShowTranscription`), а речь ученику недоступна — переключатель здесь
  // не сработал бы ни при каком наборе настроек.
  supportsTranscription: false,
}
